import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Dispatch,
  Fragment,
  MutableRefObject,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { SuccessAlertContext } from "App";
import Notify from "config/notify";
import { OrderCheck } from "../types";
import TableCellSmall, {
  MultiTypography,
  TableCellNarrow,
  TableCellWide,
  TableHeaderCellNarrow,
  TableHeaderCellSmall,
  TableHeaderCellWide,
} from "./TableCellSmall";
import { localeStringDisZero } from "../utils/common";
import { orderStatusOptionList } from "../constants";
import Mask from "./Mask";

const limit = 10;

const styles: { [key: string]: SxProps } = {
  supplierTopCol: {
    // textAlign: "center",
    p: 0.5,
    borderBottom: "none",
  },
  supplierBottomCol: {
    // textAlign: "center",
    p: 0.5,
    top: 57,
  },
};

type Props = {
  open: boolean;
  title: string;
  content: string;
  ActionButtons: JSX.Element;
  onClose: Function;
  orderList: OrderCheck[];
  isFastLoading?: MutableRefObject<boolean>;
  getMoreStockList?: Function;
  setInit?: Dispatch<SetStateAction<boolean>>;
  isLoading?: boolean;
  isMoreLoading?: boolean;
  error?: string;
  errorTitle?: string;
};
/**
 * 注文依頼CSV更新確認モーダルのテーブル
 * @param root0 コンポーネントのプロップス
 * @returns JSX.Element
 */
export default function OrderAskConfirmTable({
  open,
  title,
  content,
  ActionButtons,
  onClose,
  orderList,
  isFastLoading,
  getMoreStockList,
  setInit,
  isLoading,
  isMoreLoading,
  error,
  errorTitle,
}: Props) {
  const [fireMoreLoadEvent, setFireMoreLoadEvent] = useState(false);
  const [adjustment, setAdjustment] = useState<number | undefined>(undefined);
  const contentRef = useRef<HTMLDivElement | undefined>(undefined);
  const innerRef = useRef<HTMLDivElement | undefined>(undefined);
  const { showAlert } = useContext(SuccessAlertContext);

  // スクロール処理
  const handleScroll = async () => {
    // ロード中は処理を行わない。stateはaddEventListenerで登録しているので効かず、代わりにrefを使う。
    if (isFastLoading?.current || !contentRef.current) {
      return;
    }
    const scrollTop = contentRef.current?.scrollTop;
    const contentHeight = contentRef.current?.clientHeight;
    const scrollHeight = contentRef.current?.scrollHeight;
    if (scrollTop + contentHeight + 10 >= scrollHeight) {
      // eslint-disable-next-line no-param-reassign
      if (isFastLoading) isFastLoading.current = true;
      setFireMoreLoadEvent((prev) => !prev);
    }
  };

  useEffect(() => {
    // addEventListenerするとstateの値を変更しても内部の値は変わらないため、stateを変更させるだけにし、ここで処理を行う。
    (async () => {
      if (getMoreStockList) await getMoreStockList();
    })();
  }, [fireMoreLoadEvent]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (setInit) {
      setInit(true);
      // if (error) showAlert(error, "error");
      if (error) {
        Notify?.setNotification?.({
          show: true,
          title: "CSVアップロードでエラーがあります。",
          message: error,
        });
      }
    }
  }, [open]);

  // 表示数がlimitの時、中のコンテンツが小さいと無限ローディングさせることができないため、少しスクロールできるようにする。
  useEffect(() => {
    if (orderList.length === limit && !isMoreLoading) {
      // console.log('adjustment', adjustment, orderList.length, contentRef.current?.clientHeight);
      setTimeout(() => {
        if (
          contentRef.current &&
          innerRef.current &&
          contentRef.current.clientHeight - innerRef.current.clientHeight >= 0
        ) {
          // 中のコンテンツが小さい場合、調整オブジェクトを挿入
          setAdjustment(contentRef.current.clientHeight - innerRef.current.clientHeight + 1);
        } else {
          setAdjustment(undefined);
        }
      }, 0);
    } else {
      setAdjustment(undefined);
    }
  }, [orderList, isMoreLoading]);
  return (
    <Dialog open={open} onClose={() => onClose()} fullScreen>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent ref={contentRef} onScroll={handleScroll}>
        <Box ref={innerRef}>
          <DialogContentText>{error ? errorTitle : content}</DialogContentText>
          <TableContainer
            component={Paper}
            sx={{ overflowX: "initial", width: "fit-content", minWidth: "100%" }}
          >
            <Table sx={{ width: "100%", borderCollapse: "separate" }} aria-label="order table">
              {orderList.length !== 0 && (
                <>
                  <TableHead>
                    <TableRow>
                      {/* <TableHeaderCellNarrow sx={styles.supplierTopCol}>レコードID</TableHeaderCellNarrow> */}
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        ステータス
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        注文番号
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        商品コード
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        JANコード
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        店舗名
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        仕入先名
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        大分類
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        ブランド
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol} colSpan={2}>
                        商品名
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        サイズ
                      </TableHeaderCellNarrow>
                      <TableHeaderCellWide isTwoHeight sx={styles.supplierTopCol}>品番</TableHeaderCellWide>
                      <TableHeaderCellWide isTwoHeight right sx={styles.supplierTopCol}>
                        発注数
                      </TableHeaderCellWide>
                      {error && <TableHeaderCellSmall rowSpan={2}>エラー内容</TableHeaderCellSmall>}
                    </TableRow>
                    <TableRow>
                      <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
                        本部原単価
                        {/* <br />
                        (単価) */}
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
                        仕切単価
                        {/* <br />
                        (単価) */}
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
                        店舗売単価
                        {/* <br />
                        (単価) */}
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierBottomCol}>
                        注文日
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierBottomCol}>
                        納品希望日
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierBottomCol}>
                        出荷日
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierBottomCol}>
                        出荷予定日
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierBottomCol}>
                        仕入先
                        <br />
                        入荷予定
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierBottomCol}>
                        得意先伝票No.
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierBottomCol}>
                        客注
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierBottomCol}>
                        直送
                      </TableHeaderCellNarrow>
                      <TableHeaderCellWide sx={styles.supplierBottomCol}>
                        店舗備考
                      </TableHeaderCellWide>
                      <TableHeaderCellWide sx={styles.supplierBottomCol}>
                        本部備考
                      </TableHeaderCellWide>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {orderList.map((order, index) => {
                      const isZebraStripeRow = !(error && order.error) ? (index % 2 === 1) : false;
                      return (
                      <>
                        <TableRow
                          key={order.productCd}
                          sx={error && order.error ? { backgroundColor: "var(--red-400)" } : {}}
                        >
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.orderStatus === orderStatusOptionList[9] ? orderStatusOptionList[2] : order.orderStatus}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.orderNumber}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.productCd}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.janCd}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.shopName}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.supplierName}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.classification}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.brand}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow} colSpan={2}>{order.productName}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.size}</TableCellNarrow>
                          <TableCellWide multi gray={isZebraStripeRow}>{order.productNumber}</TableCellWide>
                          <TableCellWide right multi gray={isZebraStripeRow}>{localeStringDisZero(order.orderCount)}</TableCellWide>
                          {error &&
                            (order.error ? (
                              <TableCellWide rowSpan={2}>
                                <MultiTypography><>{
                                  // eslint-disable-next-line react/no-array-index-key
                                  order.error.split('\n').map((e,i) => (i === 0 ? e : <Fragment key={i}><br/>{e}</Fragment>))
                                }</></MultiTypography>
                              </TableCellWide>
                            ) : (
                              <TableCellWide rowSpan={2} />
                            ))
                          }
                        </TableRow>
                        <TableRow
                          sx={(error && order.error) ? { backgroundColor: "var(--red-400)" } : {}}
                        >
                          <TableCellNarrow right multi gray={isZebraStripeRow}>
                            {/* {localeStringDisZero(order.headquartersCost)}<br /> */}
                            {`${localeStringDisZero(order.headquartersUnitCost)}`}
                          </TableCellNarrow>
                          <TableCellNarrow right multi gray={isZebraStripeRow}>
                            {/* {localeStringDisZero(order.wholesalePrice)}<br /> */}
                            {`${localeStringDisZero(order.wholesaleUnitPrice)}`}
                          </TableCellNarrow>
                          <TableCellNarrow right multi gray={isZebraStripeRow}>
                            {/* {localeStringDisZero(order.shopPrice)}<br /> */}
                            {`${localeStringDisZero(order.shopUnitPrice)}`}
                          </TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.orderDate}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.deliveryDate}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.shippingDate}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.expectedShippingDate}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.arrivalDate}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.clientBillNumber}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.isCustomerOrder ? '〇' : '×'}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.isDirectOrder ? '〇' : '×'}</TableCellNarrow>
                          <TableCellWide multi gray={isZebraStripeRow}>{order.shopRemarks}</TableCellWide>
                          <TableCellWide multi gray={isZebraStripeRow}>{order.headquartersRemarks}</TableCellWide>
                        </TableRow>
                      </>
                    )})}
                  </TableBody>
                </>
              )}
            </Table>
          </TableContainer>
          {adjustment && ( // 画面が大きいと無限スクロールが発火しないことがある
            <Box height={adjustment} />
          )}
          {isMoreLoading && (
            <Box py={3} textAlign="center">
              <CircularProgress />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>{ActionButtons}</DialogActions>
      <Mask isLoading={isLoading} />
    </Dialog>
  );
}
